import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchPublicFeatureFlags } from "../service/FeatureFlagService";
import { ftFeatureFlags } from "../constants/Constants";

const FeatureFlagsContext = createContext({ flags: {}, isEnabled: () => true, loading: true });

// How often to re-check flags while the app stays open, so an admin toggle
// reaches users without a manual page refresh.
const POLL_MS = 30000;

// Seed from the last-known cache so gated routes paint correctly on first render
// (avoids a flash of "Under Construction" before the network call resolves).
function readCache() {
    try {
        const raw = localStorage.getItem(ftFeatureFlags);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

export function FeatureFlagsProvider({ children }) {
    const [flags, setFlags] = useState(readCache);
    const [loading, setLoading] = useState(true);
    const mountedRef = useRef(true);
    const location = useLocation();

    const refresh = useCallback(() => {
        return fetchPublicFeatureFlags()
            .then(res => {
                if (!mountedRef.current) return;
                const next = res?.flags ?? {};
                setFlags(next);
                try { localStorage.setItem(ftFeatureFlags, JSON.stringify(next)); } catch {}
            })
            .catch(() => { /* fail open — keep cached/empty flags so nothing breaks */ })
            .finally(() => { if (mountedRef.current) setLoading(false); });
    }, []);

    // Re-check on every navigation: covers the case where a user opens a section
    // that was just enabled/disabled. Also fires once on first mount.
    useEffect(() => {
        refresh();
    }, [location.pathname, refresh]);

    // Keep flags fresh while the user sits on a page: poll on an interval and
    // re-check whenever the tab regains focus/visibility.
    useEffect(() => {
        mountedRef.current = true;
        const interval = setInterval(refresh, POLL_MS);
        const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", refresh);
        return () => {
            mountedRef.current = false;
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", refresh);
        };
    }, [refresh]);

    // Fail-open: only an explicit `false` disables a section. Unknown keys, a
    // missing flag, or a failed fetch all leave the section enabled.
    const isEnabled = (key) => flags[key] !== false;

    return (
        <FeatureFlagsContext.Provider value={{ flags, isEnabled, loading, refresh }}>
            {children}
        </FeatureFlagsContext.Provider>
    );
}

export function useFeatureFlags() {
    return useContext(FeatureFlagsContext);
}
